import { useEffect, useState } from "react";
import shortid from "shortid";
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CurrencyInput,
  DataGrid,
  DatePicker,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Tab,
  TabPanel,
  Tabs,
  TextField,
  Tooltip,
} from "../../../components";
import { useImportContext } from "../../../contexts/import.context";
import { InputErros, PermisoesHelper, value } from "../../../helpers";
import { AddLayout } from "../../../layouts";
import {
  BancosCaixasResponsaveisService,
  BancosCaixasService,
  BancoService,
  EmpresaService,
  EnderecoService,
  history,
  notification,
  PlugBoletoService,
  SessionService,
  UsuarioService,
} from "../../../services";
import {
  formatDate,
  responseErros,
  unformatPrice,
  userLogs,
  voltar,
} from "../../../utils";
import {
  BancoCaixa,
  BancoCedente,
  BancoConta,
  BancoConvenio,
} from "../entities";
import FormDadosBancarios from "./components/dados-bancarios.component";
import contaValidator from "./middlewares/banco-caixa.validator";
import { alpha } from "@mui/material";

function tabProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

const AddBancosCaixas = ({ match }) => {
  const permissoesHelper = new PermisoesHelper();
  permissoesHelper.validarPermisao("bancos-caixas-visualizar");
  const id = match && match.params?.id;
  const { initialCollapses } = useImportContext();
  const [loading, setLoading] = useState(false);
  const [initialContaState, setInitialContaState] = useState({
    tipoConta: "INTERNA",
    aberturaAutomatica: false,
    ativado: true,
    caixaBanco: false,
    empresasAcessoIds: [],
  });
  const [conta, setConta] = useState({
    tipoConta: "INTERNA",
    aberturaAutomatica: false,
    ativado: true,
    caixaBanco: false,
    empresasAcessoIds: [],
  });
  const [bancos, setBancos] = useState([]);
  const [bancosHomologados, setBancosHomologados] = useState([]);
  const [listaResponsaveis, setListaResponsaveis] = useState([]);
  const [listaUsuarios, setListaUsuarios] = useState([]);
  const [usuariosPermitidos, setUsuariosPermitidos] = useState(null);
  const [listaConvenios, setListaConvenios] = useState([]);
  const [userLog, setUserLog] = useState({});
  const [endereco, setEndereco] = useState({});
  const [cidades, setCidades] = useState([]);
  const [estados, setEstados] = useState([]);
  const [cidadeId, setCidadeId] = useState(null);
  const [ufId, setUfId] = useState(null);
  const [abaValue, setAbaValue] = useState(0);
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const bancoService = new BancoService();
  const bancosCaixasService = new BancosCaixasService();
  const enderecoService = new EnderecoService();
  const bancosCaixasResponsaveisService = new BancosCaixasResponsaveisService();
  const usuarioService = new UsuarioService();
  const empresaService = new EmpresaService();
  const plugBoletoService = new PlugBoletoService();
  const sessionService = new SessionService();
  const dadosEmpresa = sessionService.getEmpresa();
  const empresasDoUsuario = sessionService.getUsuario()?.acessosEmpresas ?? [];
  const multiLoja = empresasDoUsuario.length > 1;
  const empresasOpcoes = [
    ...empresasDoUsuario,
    ...(conta.empresasAcessoIds ?? [])
      .filter(
        (empresaId) =>
          !empresasDoUsuario.some((empresa) => empresa.id === empresaId),
      )
      .map((empresaId) => ({ id: empresaId, fantasia: `Empresa ${empresaId}` })),
  ];
  const opcoesUsuarios = usuariosPermitidos ?? listaUsuarios;

  const colunas = [
    {
      field: "usuarioId",
      headerName: "Nome",
      flex: 1000,
      valueGetter: (params) =>
        value.autoComplete(listaUsuarios, params.value).nome,
    },
  ];

  async function buscarDadosIniciais() {
    await buscaConta();
  }

  useEffect(() => {
    if (id) {
      buscarDadosIniciais();
    }
    buscarUsuarios();
  }, []);

  useEffect(() => {
    buscarUsuariosPermitidos(conta.empresasAcessoIds);
  }, [conta.empresasAcessoIds?.join(",")]);

  const buscaConta = async () => {
    setLoading(true);
    const result = await bancosCaixasService.getById(id);
    if (!result.isAxiosError) {
      if (!result.data) {
        voltar();
        initialCollapses();
        return;
      }
      if (result.data.bancoId) {
        setEndereco(result.data.endereco);
        setCidadeId(result.data.endereco.cidadeId);
        buscarCidadeById(result.data.endereco.cidadeId);
        buscaBancos(result.data.banco.nome);
        buscaBancosHomologados();
      }
      const contaCarregada = {
        ...result.data,
        empresasAcessoIds:
          result.data.empresasAcesso?.map(({ pctEmpresaId }) => pctEmpresaId) ??
          [],
      };
      setInitialContaState(contaCarregada);
      setConta(contaCarregada);
      setListaResponsaveis(result.data.responsaveisList);
      setListaConvenios(result.data.convenios);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscaBancos = (nome) => {
    const filtros = {
      nome,
      nonPaginated: true,
    };
    bancoService.getAll(filtros).then((res) => {
      if (!res.isAxiosError) {
        setBancos(res.data);
      } else {
        responseErros(res);
      }
    });
  };

  const buscaBancosHomologados = () => {
    const filtros = {
      homologadoBoleto: true,
      nonPaginated: true,
    };
    bancoService.getAll(filtros).then((res) => {
      if (!res.isAxiosError) {
        setBancosHomologados(res.data);
      } else {
        responseErros(res);
      }
    });
  };

  const buscarCidadeById = async (cidadeId) => {
    enderecoService.getCidadeById(cidadeId).then((result) => {
      if (!result.isAxiosError) {
        const estadoId = result.data?.estado.id;
        setUfId(estadoId);
        changeCidadesByEstadoId(estadoId);
      } else {
        responseErros(result);
      }
    });
  };

  const changeCidadesByEstadoId = async (id) => {
    if (id) {
      enderecoService.getCidades(id).then((response) => {
        if (!response.isAxiosError) {
          if (!response.data) {
            notification.erroValidacao("Preencha um estado");
          } else {
            setCidades(response.data);
          }
        } else {
          responseErros(response);
        }
      });
    }
  };

  const buscarUsuarios = function () {
    usuarioService.getAll({ nonPaginated: true }).then((result) => {
      if (!result.isAxiosError) {
        setListaUsuarios(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarUsuariosPermitidos = async (empresasAcessoIds) => {
    if (!empresasAcessoIds?.length) {
      setUsuariosPermitidos(null);
      return;
    }
    const result = await usuarioService.getAll({
      acessoEmpresasIds: empresasAcessoIds,
      nonPaginated: true,
    });
    if (!result.isAxiosError) {
      setUsuariosPermitidos(result.data);
    } else {
      responseErros(result);
    }
  };

  const onChangeEmpresasAcesso = (empresas) => {
    setConta((prevState) => ({
      ...prevState,
      empresasAcessoIds: empresas.map((empresa) => empresa.id),
    }));
  };

  const validarEmpresasAcesso = async () => {
    if (!conta.empresasAcessoIds?.length) return true;
    if (!usuariosPermitidos) {
      notification.alertaGenericos(
        "Aguarde a validação dos responsáveis e tente novamente",
      );
      return false;
    }
    const responsaveisSemAcesso = listaResponsaveis.filter(
      (responsavel) =>
        !usuariosPermitidos.some(
          (usuario) => usuario.id === responsavel.usuarioId,
        ),
    );
    if (responsaveisSemAcesso.length) {
      const nomes = responsaveisSemAcesso
        .map(
          (responsavel) =>
            value.autoComplete(listaUsuarios, responsavel.usuarioId)?.nome,
        )
        .filter(Boolean)
        .join(", ");
      notification.alertaGenericos(
        `Responsáveis sem acesso a todas as empresas do caixa: ${nomes}`,
      );
      return false;
    }
    if (!conta.empresasAcessoIds.includes(dadosEmpresa?.id)) {
      const retornoAlerta = await notification.confirmacao(
        "Empresa atual fora do caixa",
        "Esta empresa não está entre as empresas com acesso. Depois de salvar, o caixa deixará de aparecer aqui. Deseja continuar?",
      );
      return retornoAlerta.isConfirmed;
    }
    return true;
  };

  async function onClickLog() {
    if ("userCreatedLog" in userLog) return;
    const logs = await userLogs(conta);
    setUserLog(logs);
  }

  const handleInputChange = (event) => {
    setConta((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };

  const handleInputBlur = (e) => {
    e.persist();
    setConta((prevState) => ({
      ...prevState,
      [e.target.name]: unformatPrice(e.target.value),
    }));
  };

  const handleDateChange = (name, date) => {
    if (!date) {
      date = "Invalid Date";
    }
    setConta((prevState) => ({
      ...prevState,
      [name]: date != "Invalid Date" ? formatDate.toSend(date) : null,
    }));
  };

  const handleSubmit = async (indiceSelecionado) => {
    const empresasValidas = await validarEmpresasAcesso();
    if (!empresasValidas) return;
    setLoading(true);
    const dataBanco = {
      ...conta,
      endereco: {
        ...endereco,
        cidadeId,
      },
      responsaveisList: listaResponsaveis,
      convenios: listaConvenios,
    };
    contaValidator
      .validate(dataBanco, { abortEarly: false })
      .then(async () => {
        const bancoCaixa = new BancoCaixa(dataBanco, id);
        const emailValido = bancoCaixa.validarEmail();
        if (!emailValido && conta.emailGerente) {
          const erro = {
            errors: ["E-mail inválido"],
            inner: [{ path: "emailGerente" }],
          };
          inputErros.set(erro);
          setLoading(false);
          return false;
        }
        if (conta?.id) {
          try {
            if (conta.boletoHabilitado) {
              const bancoConta = new BancoConta(conta);
              const apiResponseConta = await plugBoletoService.atualizarConta(
                conta.integradorContaId,
                bancoConta,
                dadosEmpresa.cnpjCpf,
              );
              if (!apiResponseConta || apiResponseConta._status === "erro") {
                throw new Error(
                  apiResponseConta?._dados?.[0]?._erro ??
                    "Erro interno de servidor",
                );
              }
            }
            const response = await bancosCaixasService.atualizar(
              conta?.id,
              bancoCaixa,
            );
            setLoading(false);
            if (!response.isAxiosError) {
              notification.alteracaoSucesso();
              history.goBack();
            } else {
              responseErros(response);
            }
          } catch (error) {
            setLoading(false);
            notification.erroGenericos(error.message);
          }
        } else {
          const response = await bancosCaixasService.cadastrar(bancoCaixa);
          setLoading(false);
          if (!response.isAxiosError) {
            notification.cadastroSucesso();
            if (indiceSelecionado === 0) {
              history.goBack();
            } else {
              handleCancelar();
            }
          } else {
            responseErros(response);
          }
        }
      })
      .catch((err) => {
        setLoading(false);
        inputErros.set(err);
      });
  };

  const handleCancelar = () => {
    setConta(initialContaState);
  };

  const handleAdicionarUsuario = (idUser) => {
    const usuarioInserido = listaResponsaveis.find(
      (item) => item.usuarioId == idUser,
    );
    if (usuarioInserido) {
      notification.alertaGenericos("Usuario já inserido");
    } else {
      setConta({
        ...conta,
        usuarioId: null,
      });
      const novoResponsavel = {
        id: shortid.generate(),
        usuarioId: idUser,
        bancoCaixaId: id,
      };
      if (id) {
        handleSubmitResponsavel(novoResponsavel);
      } else setListaResponsaveis([...listaResponsaveis, novoResponsavel]);
    }
  };

  const handleSubmitResponsavel = async (responsavel) => {
    setLoading(true);
    if (responsavel) {
      const response = await bancosCaixasResponsaveisService.cadastrar(
        responsavel,
      );
      if (!response.isAxiosError) {
        buscaConta();
        notification.cadastroSucesso();
      } else responseErros(response);
    }
    setLoading(false);
  };

  const handleRemoverUsuario = async (idUser) => {
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      `Tem certeza que deseja excluir esse usuário?`,
    );
    if (retornoAlerta.isConfirmed) {
      if (id) {
        if (listaResponsaveis?.length > 1) {
          setLoading(true);
          const result = await bancosCaixasResponsaveisService.deletar(idUser);
          if (!result.isAxiosError) {
            buscaConta();
            notification.deletadoSucesso();
          } else responseErros(result);
          setLoading(false);
        } else
          notification.alertaGenericos(
            "Pelo menos 1 responsáveis é obrigatório",
          );
      } else {
        const data = listaResponsaveis.filter((item) => item.id !== idUser);
        setListaResponsaveis(data);
      }
    }
  };

  const onChangeAutocomplete = (name, value) => {
    setConta({
      ...conta,
      [name]: value ? value.id : undefined,
    });
  };

  const mensagemErroEmissaoBoleto = () => {
    if (!bancosHomologados.find((item) => item.id === conta.bancoId)) {
      return "O banco selecionado não pode ser utilizado para emissão de boletos";
    }
    if (!conta.convenios.length) {
      return "É necessário pelo menos um convênio associado à conta bancária";
    }

    return null;
  };

  const habilitarEmissaoBoleto = async () => {
    try {
      setLoading(true);
      const mensagemErro = mensagemErroEmissaoBoleto();
      if (mensagemErro) {
        setLoading(false);
        notification.alertaGenericos(mensagemErro);
        return;
      }

      const empresa = await empresaService.get();
      if (!empresa.data.cedenteId) {
        const cedente = new BancoCedente(empresa.data);
        const apiResponse = await plugBoletoService.cadastrarCedente(cedente);
        if (apiResponse?._status === "sucesso") {
          const dados = apiResponse._dados;
          const response = await empresaService.atualizarInformacoesCedente(
            empresa.data.id,
            { cedenteId: dados.id, cedenteToken: dados.token_cedente },
          );
          if (response.isAxiosError || !response.data) {
            responseErros(response);
            return;
          }
        } else {
          throw new Error(apiResponse?._mensagem ?? "Erro interno de servidor");
        }
      }

      let contaId = conta.integradorContaId;
      if (!contaId) {
        const bancoConta = new BancoConta(conta);
        const apiResponseConta = await plugBoletoService.cadastrarConta(
          bancoConta,
          empresa.data.cnpjCpf,
        );
        if (apiResponseConta?._status === "sucesso") {
          contaId = apiResponseConta._dados.id;
        } else {
          throw new Error(apiResponseConta?._mensagem ?? "Erro interno de servidor");
        }
      }

      const updateContaBody = { integradorContaId: contaId },
        updateConveniosBody = [];
      for (const convenio of conta.convenios) {
        if (!convenio.integradorConvenioId) {
          const bancoConvenio = new BancoConvenio(convenio, contaId);
          const apiResponseConvenio = await plugBoletoService.cadastrarConvenio(
            bancoConvenio,
            empresa.data.cnpjCpf,
          );
          if (apiResponseConvenio?._status === "sucesso") {
            updateConveniosBody.push({
              id: convenio.id,
              integradorConvenioId: apiResponseConvenio._dados.id,
            });
          } else {
            const body = {
              ...updateContaBody,
              convenios: updateConveniosBody,
            };
            await bancosCaixasService.atualizarInformacoesIntegrador(id, body);
            throw new Error(
              apiResponseConvenio?._dados?.[0]?._erro ??
                "Erro interno de servidor",
            );
          }
        }
      }
      const body = {
        ...updateContaBody,
        boletoHabilitado: true,
        convenios: updateConveniosBody,
      };
      await bancosCaixasService.atualizarInformacoesIntegrador(id, body);
      notification.sucessoGenericos(
        "Emissão de boletos habilitada com sucesso!",
      );
    } catch (error) {
      notification.erroGenericos(error?.message);
    } finally {
      await buscarDadosIniciais();
      setLoading(false);
    }
  };

  const AtivadoComponent = ({ conta, setConta }) => {
    return (
      <FormControlLabel
        control={
          <Switch
            checked={conta?.ativado ? conta.ativado : false}
            onChange={() =>
              setConta((conta) => {
                return {
                  ...conta,
                  ativado: !conta?.ativado,
                };
              })
            }
            name="ativado"
            color="primary"
          />
        }
        label={conta?.ativado ? "Ativado" : "Desativado"}
      />
    );
  };

  const renderButtonEmissaoBoleto = () => {
    return conta?.boletoHabilitado ? (
      <Tooltip
        title="Esta conta bancária está habilitada para emitir boletos"
        arrow
        placement="top"
        className="m-3 me-4"
      >
        <Chip
          className="d-flex p-1"
          key={shortid.generate()}
          icon={<i className="ph-fill ph-circle"></i>}
          label="Emissão Habilitada"
          sx={{
            backgroundColor: alpha("#4FBB53", 0.08),
            color: "#000",
            ".MuiChip-icon": {
              color: "#4FBB53",
            },
          }}
        />
      </Tooltip>
    ) : (
      <Button
        className="m-3 me-4"
        variant="outlined"
        color="primary"
        size="small"
        onClick={habilitarEmissaoBoleto}
      >
        Habilitar Boleto
      </Button>
    );
  };

  return (
    <AddLayout
      id={id}
      title={conta?.id ? "Editar Caixa" : "Cadastrar Caixa"}
      onClickSalvar={handleSubmit}
      loading={loading}
      userLog={conta?.id ? userLog : null}
      onClickLog={onClickLog}
      actions={
        <>
          {id &&
            conta?.caixaBanco &&
            conta?.ativado &&
            permissoesHelper.temPermisao("habilitar-banco-boleto") &&
            conta?.emissaoBoleto &&
            renderButtonEmissaoBoleto()}
          <AtivadoComponent conta={conta} setConta={setConta} />
        </>
      }
      showUpdate={permissoesHelper.temPermisao("bancos-caixas-editar")}
    >
      <Grid container spacing={2}>
        <Grid item xs={5}>
          <TextField
            id="nome"
            name="nome"
            label="Nome Conta"
            variant="outlined"
            margin="normal"
            value={value.text(conta?.nome)}
            onChange={(e) => {
              setInputErro([]);
              handleInputChange(e);
            }}
            fullWidth
            required
            error={inputErros.get("nome")}
          />
        </Grid>
        <Grid item xs={2}>
          <DatePicker
            id="dataSaldoInicial"
            name="dataSaldoInicial"
            label="Data Saldo Inicial"
            format="dd/MM/yyyy"
            margin
            value={value.date(conta?.dataSaldoInicial)}
            onChange={(date) => handleDateChange("dataSaldoInicial", date)}
            error={inputErros.get("dataSaldoInicial")}
          />
        </Grid>
        <Grid item xs={2}>
          <CurrencyInput
            id="saldoInicial"
            name="saldoInicial"
            label="Saldo Inicial (R$)"
            variant={id ? "filled" : "outlined"}
            disabled={id ? true : false}
            margin="normal"
            value={conta.saldoInicial ?? null}
            onBlur={(e) => {
              setInputErro([]);
              handleInputBlur(e);
            }}
            fullWidth
            error={inputErros.get("saldoInicial")}
          />
        </Grid>
        <Grid item xs={3} className="d-flex">
          <FormControlLabel
            className="aberturaAutomatica"
            control={
              <Checkbox
                checked={
                  conta?.aberturaAutomatica ? conta.aberturaAutomatica : false
                }
                value={conta?.aberturaAutomatica}
                onChange={() => {
                  setConta((prevState) => ({
                    ...prevState,
                    aberturaAutomatica: !conta?.aberturaAutomatica,
                  }));
                }}
                name="aberturaAutomatica"
                color="primary"
              />
            }
            label="Abertura Automática"
          />
        </Grid>
        <Grid item xs={3} className="d-flex">
          <FormControlLabel
            className="aberturaAutomatica"
            control={
              <Checkbox
                checked={conta?.caixaBanco ? conta.caixaBanco : false}
                value={conta?.caixaBanco}
                onChange={() => {
                  if (conta?.caixaBanco) setAbaValue(0);
                  setConta((prevState) => ({
                    ...prevState,
                    caixaBanco: !conta?.caixaBanco,
                  }));
                }}
                name="caixaBanco"
                color="primary"
                disabled={conta?.boletoHabilitado}
              />
            }
            label="Caixa Banco"
          />
        </Grid>
      </Grid>
      {multiLoja && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              id="empresasAcessoIds"
              name="empresasAcessoIds"
              options={empresasOpcoes}
              filterSelectedOptions
              getOptionLabel={(option) => option?.fantasia ?? ""}
              isOptionEqualToValue={(option, empresa) =>
                option.id === empresa.id
              }
              onChange={(_, empresas) => onChangeEmpresasAcesso(empresas)}
              value={empresasOpcoes.filter((empresa) =>
                conta.empresasAcessoIds?.includes(empresa.id),
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Empresas com acesso ao caixa"
                  margin="normal"
                  variant="outlined"
                  helperText="Em branco, o caixa continua disponível para todas as empresas"
                />
              )}
            />
          </Grid>
        </Grid>
      )}
      <Paper sx={{ mt: 1 }}>
        <Tabs
          value={abaValue}
          onChange={(_, value) => setAbaValue(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Responsáveis" {...tabProps(0)} />
          <Tab
            label="Dados Bancários"
            {...tabProps(1)}
            disabled={!conta?.caixaBanco}
          />
        </Tabs>
      </Paper>
      <TabPanel className="tabPanel" value={abaValue} index={0}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControl variant="outlined" fullWidth>
              <Autocomplete
                id="usuarioId"
                name="usuarioId"
                noOptionsText={"Buscar usuario..."}
                options={opcoesUsuarios}
                autoHighlight
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Usuários"
                    margin="normal"
                    required
                    variant="outlined"
                    error={inputErros.get("usuarioId")}
                  />
                )}
                getOptionLabel={(option) => (option.nome ? option.nome : "")}
                filterSelectedOptions
                onChange={(e, newValue) => {
                  onChangeAutocomplete("usuarioId", newValue);
                }}
                value={value.autoComplete(opcoesUsuarios, conta.usuarioId)}
              />
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <Button
              className="mt-4"
              variant="contained"
              color="primary"
              onClick={() => {
                handleAdicionarUsuario(conta.usuarioId);
              }}
            >
              <i
                className="ph-fill ph-plus-circle"
                style={{ color: "#fff", fontSize: 22, marginRight: 10 }}
              />
              Adicionar
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={2} className="mt-3">
          <Grid item xs={12}>
            <DataGrid
              columns={colunas}
              rows={listaResponsaveis}
              rowCount={listaResponsaveis.length}
              deleteShow={permissoesHelper.temPermisao([
                "bancos-caixas-cadastrar",
                "bancos-caixas-editar",
              ])}
              onClickDelete={handleRemoverUsuario}
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel className="tabPanel" value={abaValue} index={1}>
        <FormDadosBancarios
          conta={conta}
          setConta={setConta}
          bancos={bancos}
          buscaBancos={buscaBancos}
          endereco={endereco}
          setEndereco={setEndereco}
          cidades={cidades}
          estados={estados}
          setEstados={setEstados}
          cidadeId={cidadeId}
          setCidadeId={setCidadeId}
          ufId={ufId}
          setUfId={setUfId}
          inputErros={inputErros}
          changeCidadesByEstadoId={changeCidadesByEstadoId}
          onChangeInput={handleInputChange}
          permissoesHelper={permissoesHelper}
          listaConvenios={listaConvenios}
          setListaConvenios={setListaConvenios}
          dadosEmpresa={dadosEmpresa}
        />
      </TabPanel>
    </AddLayout>
  );
};

export default AddBancosCaixas;
